import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Standard time periods for procurement/spend analysis
 * Default: 3Yr (1095 days)
 *
 * Note: 2y added in Slice 2 (date-range roadmap) to match prototype UX spec.
 */
export const STANDARD_TIME_PERIODS = [
    { value: '1m', label: '1Mo', days: 30 },
    { value: '3m', label: '3Mo', days: 90 },
    { value: '6m', label: '6Mo', days: 182 },
    { value: '1y', label: '1Yr', days: 365 },
    { value: '2y', label: '2Yr', days: 730 },
    { value: '3y', label: '3Yr', days: 1095, default: true },
    { value: '5y', label: '5Yr', days: 1825 },
    { value: 'all', label: 'All', days: null },
];
/**
 * Calculate the start date for a given time period
 * @param period - The time period value (e.g., '3y', 'all')
 * @param periods - Array of time periods to look up (defaults to STANDARD_TIME_PERIODS)
 * @returns Date object for start of range, or null for 'all time'
 */
export function calculateDateRange(period, periods = STANDARD_TIME_PERIODS) {
    const p = periods.find(t => t.value === period);
    if (!p || p.days === null)
        return null;
    const d = new Date();
    d.setDate(d.getDate() - p.days);
    return d;
}
/**
 * Get the default time period value from a periods array
 */
export function getDefaultTimePeriod(periods = STANDARD_TIME_PERIODS) {
    return periods.find(p => p.default)?.value || periods[0]?.value || '3y';
}
/**
 * TimeFilter - Pill button group for selecting time periods
 *
 * MUI-free, uses CSS variables for theming.
 * Works with any accent color via --accent CSS variable.
 */
export function TimeFilter({ value, onChange, periods = STANDARD_TIME_PERIODS, showIcon = false, size = 'sm', className = '', disabled = false, }) {
    const isSm = size === 'sm';
    return (_jsxs("div", { style: { ...styles.container, ...(disabled ? styles.disabled : {}) }, className: className, children: [showIcon && (_jsxs("svg", { width: isSm ? 14 : 16, height: isSm ? 14 : 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: styles.icon, children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] })), periods.map(p => {
                const isActive = value === p.value;
                return (_jsx("button", { style: {
                        ...styles.button,
                        ...(isSm ? styles.buttonSm : styles.buttonMd),
                        ...(isActive ? styles.buttonActive : {}),
                    }, onClick: () => !disabled && onChange(p.value), disabled: disabled, type: "button", "aria-pressed": isActive, children: p.label }, p.value));
            })] }));
}
const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        background: 'var(--surface-2, #f3f4f6)',
        borderRadius: '6px',
        padding: '2px',
    },
    disabled: {
        opacity: 0.5,
        pointerEvents: 'none',
    },
    icon: {
        color: 'var(--text-muted, #6b7280)',
        marginLeft: '6px',
        marginRight: '2px',
    },
    button: {
        fontWeight: 500,
        border: 'none',
        borderRadius: '4px',
        background: 'transparent',
        color: 'var(--text-muted, #6b7280)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
    },
    buttonSm: {
        fontSize: '11px',
        padding: '4px 10px',
    },
    buttonMd: {
        fontSize: '13px',
        padding: '6px 14px',
    },
    buttonActive: {
        background: 'var(--accent, #3b82f6)',
        color: 'white',
    },
};
export default TimeFilter;
